import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import type { ChapterMasteryStudent, ClassPerfChapterMasteryResponse } from '../../../api/types';
import { bandFor } from '../../../lib/bandColors';
import { PagedFooter, SortableTh, Term } from '../../../components/ui';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { usePagination } from '../../../lib/usePagination';
import { shortSection } from '../lib/cpBits';

export type ChapterOrder = 'name' | 'weakest';

interface Props {
  data: ClassPerfChapterMasteryResponse;
  selected?: number[];
  onToggleChapter?: (id: number) => void;
  /** Column order: data order, or weakest class average first. */
  order?: ChapterOrder;
  /** "Grade 10", for the `· 10B` suffix after a student's name. */
  gradeName?: string;
  /** Section picked in the page filters; names the suffix when the rows carry no section of their own. */
  filteredSectionName?: string;
}

// One sample value per band, in scale order, so the legend always matches bandFor.
const LEGEND: Array<{ label: string; sample: number | null }> = [
  { label: 'Below 50%', sample: 40 },
  { label: '50–70%', sample: 60 },
  { label: '70–85%', sample: 75 },
  { label: '85% or higher', sample: 90 },
  { label: 'No attempts', sample: null },
];

export default function ChapterMasteryTable({ data, selected, onToggleChapter, order = 'name', gradeName, filteredSectionName }: Props) {
  const cellMap = useMemo(() => {
    const m: Record<string, { pct: number; attempts: number }> = {};
    for (const c of data.cells) m[`${c.student_id}-${c.chapter_id}`] = c;
    return m;
  }, [data.cells]);

  const avgByCh = useMemo(() => {
    const m: Record<number, number | null> = {};
    for (const a of data.class_avg_per_chapter) m[a.chapter_id] = a.pct;
    return m;
  }, [data.class_avg_per_chapter]);

  // "Unit N" is the chapter's place in the subject's own order, kept when the columns are reordered.
  const unitOf = useMemo(() => {
    const m: Record<number, number> = {};
    data.chapters.forEach((ch, i) => { m[ch.id] = i + 1; });
    return m;
  }, [data.chapters]);

  // Independent from the shared row-sort hook: this only reorders the chapter
  // *columns* (header + every row's cells + the Class avg footer), never the
  // student rows.
  const orderedChapters = useMemo(() => {
    if (order !== 'weakest') return data.chapters;
    return [...data.chapters].sort((a, b) => {
      const av = avgByCh[a.id], bv = avgByCh[b.id];
      const an = av == null ? Infinity : av;
      const bn = bv == null ? Infinity : bv;
      return an - bn;
    });
  }, [data.chapters, order, avgByCh]);

  const columns = useMemo<SortColumn<ChapterMasteryStudent>[]>(() => [
    { key: 'student', accessor: (s) => `${s.first_name} ${s.last_name}`.trim() || s.username },
    { key: 'row_avg', accessor: (s) => s.avg, type: 'number' as const },
  ], []);

  const { rows, sort, toggle } = useSortableTable(data.students, columns, { key: 'student', dir: 'asc' });

  const pg = usePagination(rows, 10, `${sort?.key}${sort?.dir}`);
  const selectedSet = new Set(selected ?? []);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] border-separate [border-spacing:4px] text-sm">
          <thead>
            <tr className="text-left">
              <SortableTh columnKey="student" sort={sort} onToggle={toggle} className="sticky left-0 z-[1] w-[180px] bg-surface px-0.5 pb-1 pt-1 align-bottom">
                Student
              </SortableTh>
              {orderedChapters.map((ch) => {
                const on = selectedSet.has(ch.id);
                return (
                  <th key={ch.id} scope="col" className="min-w-[72px] p-0 align-bottom">
                    {onToggleChapter ? (
                      <button type="button" role="checkbox" aria-checked={on} aria-label={`select ${ch.name}`}
                        onClick={() => onToggleChapter(ch.id)}
                        className={`flex min-h-[76px] w-full flex-col items-start gap-2 rounded-[12px] border p-[9px_10px] text-left text-[13px] font-semibold normal-case leading-[1.25] tracking-normal text-ink hover:border-on-navy-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                          on ? 'border-navy bg-panel dark:border-mist-fixed' : 'border-line-strong bg-surface'}`}>
                        <span className="flex items-center gap-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted">
                          <span aria-hidden className={`grid h-4 w-4 flex-none place-items-center rounded-[5px] border-[1.5px] ${
                            on ? 'border-navy bg-navy text-white dark:border-mist-fixed' : 'border-on-navy-muted bg-surface text-transparent'}`}>
                            <Check size={11} strokeWidth={3} className={on ? 'opacity-100' : 'opacity-0'} />
                          </span>
                          Unit {unitOf[ch.id]}
                        </span>
                        <span>{ch.name}</span>
                      </button>
                    ) : (
                      <span className="block px-0.5 py-1 font-semibold">{ch.name}</span>
                    )}
                  </th>
                );
              })}
              <SortableTh columnKey="row_avg" sort={sort} onToggle={toggle} align="right" help="row_avg" className="pb-1 pt-1 align-bottom">
                Row avg
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((s) => {
              const fullName = `${s.first_name} ${s.last_name}`.trim() || s.username;
              const secName = s.section?.name ?? filteredSectionName;
              const suffix = shortSection(gradeName, secName);
              return (
                <tr key={s.user_id}>
                  <td className="sticky left-0 z-[1] whitespace-nowrap bg-surface py-1.5 pl-0 pr-3">
                    <Link to={`/students/profile/${s.user_id}`}
                      className="font-semibold text-ink no-underline hover:underline hover:underline-offset-[3px]">
                      {fullName}
                    </Link>
                    {suffix && <span className="text-xs text-muted"> · {suffix}</span>}
                  </td>
                  {orderedChapters.map((ch) => {
                    const c = cellMap[`${s.user_id}-${ch.id}`];
                    const pct = c?.pct;
                    const style = bandFor(pct);
                    return (
                      <td key={ch.id} className="p-0" title={c ? `${c.attempts} attempt(s)` : 'No attempts'}>
                        <div className={`grid h-10 min-w-[72px] place-items-center rounded-[9px] font-mono text-[13px] font-semibold tabular-nums ${style.bg} ${style.fg} ${
                          selectedSet.has(ch.id) ? 'ring-2 ring-inset ring-navy dark:ring-mist-fixed' : ''}`}>
                          {pct == null ? '—' : pct.toFixed(0)}
                        </div>
                      </td>
                    );
                  })}
                  <td className="pl-3 pr-1 text-right font-mono text-[13px] font-semibold tabular-nums text-ink">
                    {s.avg == null ? '—' : s.avg.toFixed(0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="sticky left-0 z-[1] whitespace-nowrap border-t-2 border-line bg-surface pt-1 font-semibold text-ink">
                <Term k="class_avg">Class avg</Term>
              </td>
              {orderedChapters.map((ch) => {
                const v = avgByCh[ch.id];
                return (
                  <td key={ch.id} className="h-9 border-t-2 border-line pt-1 text-center font-mono text-[13px] font-semibold tabular-nums text-ink">
                    {v == null ? '—' : v.toFixed(0)}
                  </td>
                );
              })}
              <td className="border-t-2 border-line pt-1" />
            </tr>
          </tfoot>
        </table>
      </div>
      <ul aria-label="Colour legend" className="mt-3.5 flex flex-wrap gap-4 text-xs text-muted">
        {LEGEND.map((l) => (
          <li key={l.label} className="inline-flex items-center gap-1.5">
            <span aria-hidden className={`inline-block h-3 w-3 rounded-[4px] ${bandFor(l.sample).bg} ${l.sample == null ? 'border border-line-strong' : ''}`} />
            {l.label}
          </li>
        ))}
      </ul>
      <PagedFooter className="mt-3.5" label="Students pagination" {...pg} onPage={pg.setPage} />
    </div>
  );
}
